import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { ordersAPI, leadsAPI, messagesAPI, disputesAPI, aalignAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Download,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeData, setDisputeData] = useState({ reason: '', description: '' });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [orderRes, leadsRes, messagesRes] = await Promise.all([
        ordersAPI.getById(id),
        leadsAPI.getByOrder(id),
        messagesAPI.getByOrder(id),
      ]);
      setOrder(orderRes.data);
      setLeads(leadsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/buyer/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-sky-100 text-sky-800',
      in_fulfillment: 'bg-blue-100 text-blue-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await messagesAPI.send({
        order_id: id,
        text: newMessage,
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await leadsAPI.downloadCSV(id);
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleSyncToAalign = async () => {
    setSyncing(true);
    try {
      // First sync campaign
      await aalignAPI.syncCampaign(id);
      // Then sync leads
      const leadsResult = await aalignAPI.syncLeads(id);
      toast.success(leadsResult.data.message || 'Synced to AALIGN');
      fetchData();
    } catch (error) {
      toast.error('Failed to sync to AALIGN');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateDispute = async () => {
    if (!disputeData.reason || !disputeData.description) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      await disputesAPI.create({
        order_id: id,
        reason: disputeData.reason,
        description: disputeData.description,
      });
      toast.success('Dispute raised successfully');
      setDisputeOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to raise dispute');
    }
  };

  if (loading || !order) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="h-64 bg-slate-200 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  const validLeads = leads.filter(l => l.validation_status === 'valid');
  const deliveryProgress = (order.leads_delivered / order.quantity) * 100;

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="order-detail-page">
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/buyer/dashboard')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{order.campaign_name}</h1>
              <Badge className={getStatusColor(order.status)}>
                {order.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-slate-600">{order.listing_title}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSyncToAalign} disabled={syncing} data-testid="sync-aalign-btn">
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync to AALIGN
            </Button>
            {validLeads.length > 0 && (
              <Button onClick={handleDownloadCSV} data-testid="download-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            )}
          </div>
        </div>

        {/* Delivery Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Delivery Progress</span>
              <span className="text-sm text-slate-600">
                {order.leads_delivered} / {order.quantity} leads
              </span>
            </div>
            <Progress value={deliveryProgress} className="h-3" />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>0%</span>
              <span>{Math.round(deliveryProgress)}% Complete</span>
              <span>100%</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="leads">
              <TabsList>
                <TabsTrigger value="leads">Leads ({validLeads.length})</TabsTrigger>
                <TabsTrigger value="messages">Messages ({messages.length})</TabsTrigger>
                <TabsTrigger value="details">Order Details</TabsTrigger>
              </TabsList>

              <TabsContent value="leads" className="mt-4">
                <Card>
                  <CardContent className="p-0">
                    {validLeads.length === 0 ? (
                      <div className="text-center py-12">
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-medium text-slate-700 mb-2">No leads delivered yet</h3>
                        <p className="text-sm text-slate-500">Leads will appear here once delivered by the seller</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead>Job Title</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {validLeads.map((lead) => (
                              <TableRow key={lead.id}>
                                <TableCell className="font-medium">
                                  {lead.first_name} {lead.last_name}
                                </TableCell>
                                <TableCell>{lead.email}</TableCell>
                                <TableCell>{lead.company_name}</TableCell>
                                <TableCell>{lead.job_title}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-emerald-600">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Valid
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <ScrollArea className="h-[300px] mb-4 pr-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          No messages yet. Start a conversation with the seller.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`message-bubble ${msg.sender_id === user.id ? 'sent' : 'received'}`}
                              >
                                <div className="text-xs opacity-70 mb-1">{msg.sender_name}</div>
                                <div>{msg.text}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        data-testid="message-input"
                      />
                      <Button onClick={handleSendMessage} disabled={sendingMessage}>
                        {sendingMessage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-slate-500">Order ID</div>
                        <div className="font-mono text-sm">{order.id.slice(0, 8)}...</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Created</div>
                        <div>{new Date(order.created_at).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Package</div>
                        <div>{order.selected_tier}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Total Price</div>
                        <div className="font-semibold">${order.total_price.toLocaleString()}</div>
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <div className="text-sm text-slate-500 mb-2">Targeting</div>
                      <div className="flex flex-wrap gap-2">
                        {order.targeting?.regions?.map((r, i) => (
                          <Badge key={i} variant="outline">{r}</Badge>
                        ))}
                        {order.targeting?.industries?.map((ind, i) => (
                          <Badge key={i} variant="secondary">{ind}</Badge>
                        ))}
                      </div>
                    </div>
                    {order.campaign_objective && (
                      <>
                        <Separator />
                        <div>
                          <div className="text-sm text-slate-500 mb-2">Campaign Objective</div>
                          <p className="text-slate-700">{order.campaign_objective}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Seller</span>
                  <span className="font-medium">{order.seller_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Package</span>
                  <span className="font-medium">{order.selected_tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Leads</span>
                  <span className="font-medium">{order.quantity}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-medium">Total</span>
                  <span className="font-bold">${order.total_price.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* AALIGN Sync Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AALIGN Integration</CardTitle>
              </CardHeader>
              <CardContent>
                {order.aalign_campaign_id ? (
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle className="w-5 h-5" />
                    <span>Synced to AALIGN</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Clock className="w-5 h-5" />
                    <span>Not synced yet</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full text-amber-600 border-amber-200 hover:bg-amber-50">
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Raise Dispute
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Raise a Dispute</DialogTitle>
                      <DialogDescription>
                        Describe the issue with this order
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="text-sm font-medium">Reason</label>
                        <Input
                          placeholder="e.g., Invalid leads, Missing data"
                          value={disputeData.reason}
                          onChange={(e) => setDisputeData({ ...disputeData, reason: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Description</label>
                        <Textarea
                          placeholder="Describe the issue in detail..."
                          value={disputeData.description}
                          onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })}
                          rows={4}
                        />
                      </div>
                      <Button onClick={handleCreateDispute} className="w-full">
                        Submit Dispute
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetailPage;
